import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../lib/auth.tsx';
import { ago } from '../lib/format.ts';
import { useOnline } from '../lib/offline.ts';
import { pendingSaves, resolveLocalConflict, syncAllPending, type LocalSave } from '../player/saveSync.ts';
import { Icon } from './Icon.tsx';
import { useConfirm, useToast } from './ui.tsx';

/** Saves on this device waiting for the cloud (refreshes on sync events). */
export function usePendingSaves() {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['pending-saves', me.id], queryFn: () => pendingSaves(me.id), networkMode: 'always', refetchInterval: 30_000 });
  useEffect(() => {
    const refresh = () => qc.invalidateQueries({ queryKey: ['pending-saves'] });
    window.addEventListener('replay:sync', refresh);
    return () => window.removeEventListener('replay:sync', refresh);
  }, [qc]);
  return q;
}

/** Manual and automatic sync, with a clear report for the user. */
export function useSyncNow() {
  const me = useMe();
  const toast = useToast();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (silent = false) => {
      if (!online) {
        if (!silent) toast('Sin conexión: las partidas se subirán solas al recuperar Internet.', 'error');
        return;
      }
      setBusy(true);
      try {
        const r = await syncAllPending(me.id);
        if (silent && !r.synced && !r.conflicts) return;
        if (r.conflicts) toast(`${r.conflicts} ${r.conflicts === 1 ? 'partida necesita' : 'partidas necesitan'} que elijas qué versión conservar (Ajustes → Partidas).`, 'error');
        else if (r.pending) toast('No se pudo completar la sincronización. Se reintentará automáticamente.', 'error');
        else if (r.synced) toast(`${r.synced === 1 ? 'Partida sincronizada' : `${r.synced} partidas sincronizadas`} con la nube.`, 'ok');
        else if (!silent) toast('Todo está sincronizado.', 'ok');
      } finally {
        setBusy(false);
      }
    },
    [me.id, online, toast],
  );
  // Reconnection: sync automatically.
  useEffect(() => {
    const onOnline = () => void run(true);
    window.addEventListener('replay:online', onOnline);
    return () => window.removeEventListener('replay:online', onOnline);
  }, [run]);
  return { run, busy };
}

/** Console-bar status: offline badge and pending saves. */
export function ConnectionPill() {
  const online = useOnline();
  const pending = usePendingSaves().data ?? [];
  const { run, busy } = useSyncNow();
  const conflicts = pending.filter((p) => p.conflict).length;
  if (online && !pending.length) return null;
  return (
    <button
      className={`conn-pill${online ? '' : ' off'}${conflicts ? ' warn' : ''}`}
      onClick={() => void run()}
      disabled={busy}
      title={online ? 'Sincronizar partidas pendientes' : 'Sin conexión con REPLAY'}
    >
      <Icon name={online ? 'cloud' : 'cloudOff'} />
      <span>
        {!online ? 'Sin conexión' : busy ? 'Sincronizando…' : conflicts ? `${conflicts} por resolver` : `${pending.length} por subir`}
        {!online && pending.length > 0 && ` · ${pending.length} por subir`}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// PWA install
// ---------------------------------------------------------------------------
type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
let deferred: BIPEvent | null = null;
const installListeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BIPEvent;
    installListeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installListeners.forEach((l) => l());
  });
}
export function useInstallPrompt() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    installListeners.add(l);
    return () => void installListeners.delete(l);
  }, []);
  const standalone = typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches;
  return {
    canInstall: !!deferred && !standalone,
    installed: standalone,
    install: async () => {
      if (!deferred) return false;
      await deferred.prompt();
      const r = await deferred.userChoice;
      deferred = null;
      installListeners.forEach((l) => l());
      return r.outcome === 'accepted';
    },
  };
}

// ---------------------------------------------------------------------------
// Pending saves & conflicts panel (Settings → Partidas)
// ---------------------------------------------------------------------------
export function SyncPanel() {
  const pending = usePendingSaves();
  const online = useOnline();
  const { run, busy } = useSyncNow();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [working, setWorking] = useState<string | null>(null);
  const list = pending.data ?? [];

  const resolve = async (s: LocalSave, keep: 'local' | 'server') => {
    const msg =
      keep === 'local'
        ? 'La partida de este dispositivo pasará a ser la actual en todos tus dispositivos. La de la nube no se borra: queda en el historial.'
        : 'Se usará la partida de la nube. La de este dispositivo no se borra: se sube primero al historial para que puedas recuperarla.';
    if (!(await confirm(msg, { title: keep === 'local' ? 'Usar la de este dispositivo' : 'Usar la de la nube', confirmLabel: 'Continuar' }))) return;
    setWorking(s.key);
    try {
      await resolveLocalConflict(s.key, keep);
      toast('Conflicto resuelto. Ambas versiones quedan en el historial.', 'ok');
      qc.invalidateQueries({ queryKey: ['my-saves'] });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo resolver', 'error');
    } finally {
      setWorking(null);
      qc.invalidateQueries({ queryKey: ['pending-saves'] });
    }
  };

  return (
    <section className="panel">
      <div className="row between">
        <h2>Sincronización de este dispositivo</h2>
        <button className="btn btn-ghost btn-sm" onClick={() => void run()} disabled={busy || !online}>
          <Icon name="refresh" /> {busy ? 'Sincronizando…' : 'Sincronizar ahora'}
        </button>
      </div>
      {!online && <p className="notice">Sin conexión. Puedes seguir jugando: tus partidas se guardan aquí y se subirán solas al volver Internet.</p>}
      {list.length === 0 ? (
        <p className="small muted">Todas las partidas de este dispositivo están en la nube.</p>
      ) : (
        <div className="list">
          {list.map((s) => (
            <div className="list-item" key={s.key} style={{ flexWrap: 'wrap' }}>
              <Icon name={s.conflict ? 'cloudOff' : 'cloud'} />
              <div className="grow">
                <strong className="small">
                  {s.title ?? 'Partida'} · {s.kind === 'sram' ? 'partida del juego' : `estado rápido ${s.slot + 1}`}
                </strong>
                <div className="tiny faint">
                  Guardada aquí {ago(new Date(s.updatedAt).toISOString())}
                  {s.conflict
                    ? ` · en la nube hay otra versión de ${ago(s.conflict.updatedAt)}${s.conflict.deviceName ? ` (${s.conflict.deviceName})` : ''}`
                    : ' · pendiente de subir'}
                </div>
              </div>
              {s.conflict && (
                <div className="row" data-nav-row>
                  <button className="btn btn-sm" disabled={!online || working === s.key} onClick={() => void resolve(s, 'local')}>
                    Usar la de este dispositivo
                  </button>
                  <button className="btn btn-ghost btn-sm" disabled={!online || working === s.key} onClick={() => void resolve(s, 'server')}>
                    Usar la de la nube
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="tiny faint" style={{ marginTop: 'var(--s-3)' }}>
        REPLAY nunca sobrescribe una partida en silencio: si dos dispositivos guardaron por separado, tú decides cuál seguir y la otra queda en el historial de versiones.
      </p>
    </section>
  );
}
